import { usePageModels } from '@/context/PageModels';
import { slugify } from '@/utils/helpers';
import { resolveReference } from '@/utils/resolvers';
import { BrManageContentButton } from '@bloomreach/react-sdk';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { getOr, pipe } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';
import CMSPlaceholder from '../CMSPlaceholder';
import ErrorBoundary from '../ErrorBoundary';
import { HashedJumplink } from '../Jumplink';
import { useComponentProps } from './useComponentProps';

export const mapBrProps = ({ raw, mapping, page, pageModels }: any): any => {
  return Object.entries(mapping).reduce((acc, [rawKey, toMap]: any) => {
    if (!raw) {
      return acc;
    }

    let rawVal = raw[rawKey];

    if (Array.isArray(toMap)) {
      const [mapKey, mapFunc] = toMap;

      if (/object/.test(typeof mapFunc)) {
        if (rawVal && Object.keys(rawVal).includes('$ref')) {
          rawVal = resolveReference({ rawVal, page, pageModels });
        }

        return {
          ...acc,
          [mapKey || rawKey]: mapBrProps({ raw: rawVal, mapping: mapFunc, page, pageModels }),
        };
      }

      if (rawVal) {
        return {
          ...acc,
          [mapKey || rawKey]: mapFunc({ rawVal, page, pageModels, obj: raw }),
        };
      }

      return acc;
    }

    if (rawVal && Object.keys(rawVal).includes('$ref')) {
      rawVal = resolveReference({ rawVal, page, pageModels });
    }

    return { ...acc, [toMap || rawKey]: rawVal };
  }, {});
};

export const validateRequiredFields = ({
  models = {},
  requiredFields = {},
}: {
  models?: any & { componentParameterMaps?: any };
  requiredFields?: any;
}) => {
  const errors = Object.keys(requiredFields)
    .filter((item) => {
      const field = requiredFields[item];

      if (/string/.test(typeof field)) {
        return Boolean(!models[item]);
      }

      return false;
    })
    .map((item) => requiredFields[item]);

  return {
    invalid: errors.length > 0,
    erroMessage: errors[0] || '',
  };
};

const useStyles = makeStyles(
  {
    contentButtonClass: {
      position: 'absolute',
      top: 0,
      right: 0,
      '& > .hst-fab': {
        top: '5px !important',
        right: '5px',
      },
    },
  },
  { name: 'GenCMSWrapper' },
);

const CMSWrapper = ({
  brProps,
  render: ToRender,
  mapping = {},
  componentName,
  requiredFields = {},
  contentButtonNames = {},
}: {
  brProps: any;
  render: any;
  mapping?: any;
  componentName?: string;
  requiredFields?: any;
  contentButtonNames?: any;
}) => {
  const componentProps = useComponentProps(brProps);
  const classes = useStyles({});
  const { label, isHidden, hasJumplink, jumplinkLabel } = componentProps;
  const pageModels = usePageModels();
  const { component, page } = brProps || {};
  const models = component?.getModels();
  const isPreview = page?.isPreview();

  const componentIdentifier = useMemo(
    () => pipe(getOr('', ['model', 'label']), (val) => slugify({ label: val }))(component),
    [component],
  );

  const mappingProps = useMemo(
    () =>
      mapBrProps({
        raw: models,
        mapping: mapping || {},
        page,
        pageModels,
      }),
    [mapping, models, page, pageModels],
  );

  const requiredFieldsValidation: any = useMemo(
    () => validateRequiredFields({ models, requiredFields }),
    [models, requiredFields],
  );

  const getContentButtons = useCallback(
    () =>
      Object.keys(contentButtonNames)
        .filter((key) => Boolean(models[key]))
        .map((key) => {
          const document = page?.getContent(models[key]);
          return {
            key: contentButtonNames[key],
            value: (
              <div className={clsx(classes.contentButtonClass, isPreview && 'is-preview')}>
                {document && <BrManageContentButton content={document} />}
              </div>
            ),
          };
        })
        .reduce((a, b) => ({ ...a, [b.key]: b.value }), {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [models],
  );

  if (requiredFieldsValidation?.invalid || isHidden) {
    const errorMessage = isHidden
      ? 'Component <b>Hidden</b>'
      : requiredFieldsValidation.erroMessage;

    return isPreview ? (
      <CMSPlaceholder
        errorMessage={errorMessage}
        componentName={componentName || label.replace(/ component$/i, '')}
      />
    ) : (
      <span
        aria-hidden="true"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `<!-- ${componentName || label.replace(/ component$/i, '')} -->`,
        }}
      />
    );
  }

  return (
    <ErrorBoundary>
      {jumplinkLabel && <HashedJumplink hasJumplink={hasJumplink} jumplinkLabel={jumplinkLabel} />}
      <div data-component-id={componentIdentifier}>
        <ToRender
          {...mappingProps}
          isPreview={isPreview}
          contentButtons={isPreview && getContentButtons()}
          page={page}
          pageModels={pageModels}
        />
      </div>
    </ErrorBoundary>
  );
};

export default CMSWrapper;
